import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Building2,
  Users,
  MessageSquareText,
  Activity,
  Bell,
  CheckSquare,
  Handshake,
  Target,
  FileText,
  LogOut,
  Menu,
  X,
  Search,
  Flag,
  Sunrise,
  CalendarDays,
  ClipboardList,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/my-day", label: "Mein Tag", icon: Sunrise, testid: "nav-my-day" },
  { to: "/weekly-planning", label: "Wochenplanung", icon: CalendarDays, testid: "nav-weekly-planning" },
  { to: "/weekly-review", label: "Wochenrückblick", icon: ClipboardList, testid: "nav-weekly-review" },
  { to: "/companies", label: "Firmen", icon: Building2, testid: "nav-companies" },
  { to: "/contacts", label: "Personen", icon: Users, testid: "nav-contacts" },
  { to: "/challenges", label: "Herausforderungen", icon: Flag, testid: "nav-challenges" },
  { to: "/notes", label: "Gespräche", icon: MessageSquareText, testid: "nav-notes" },
  { to: "/activities", label: "Aktivitäten", icon: Activity, testid: "nav-activities" },
  { to: "/followups", label: "Wiedervorlagen", icon: Bell, testid: "nav-followups" },
  { to: "/tasks", label: "Aufgaben", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/partners", label: "Partner", icon: Handshake, testid: "nav-partners" },
  { to: "/leads", label: "Leads", icon: Target, testid: "nav-leads" },
  { to: "/quotes", label: "Angebote", icon: FileText, testid: "nav-quotes" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Global Cmd+K / Ctrl+K shortcut to open search
  useEffect(() => {
    function onKey(e) {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
      if (e.key === "/" && document.activeElement && ["INPUT", "TEXTAREA"].indexOf(document.activeElement.tagName) === -1) {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen app-canvas">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between bg-white border-b border-gray-200 px-4 h-14">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(true)} aria-label="Menü öffnen" data-testid="sidebar-open-btn">
            <Menu className="w-5 h-5" />
          </button>
          <img
            src="/branding/noras-welt-icon.png"
            alt="Noras Welt"
            className="w-7 h-7 object-contain"
          />
          <span className="font-display font-bold text-gray-900">Noras Welt</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            aria-label="Suche öffnen"
            className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
            data-testid="search-open-btn-mobile"
          >
            <Search className="w-5 h-5" />
          </button>
          <Avatar className="w-8 h-8">
            {user?.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
            <AvatarFallback className="text-xs bg-black text-white">{initials}</AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed md:sticky top-0 inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        } md:h-screen flex flex-col`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/branding/noras-welt-icon.png"
              alt="Noras Welt"
              className="w-10 h-10 object-contain shrink-0"
              data-testid="sidebar-brand-logo"
            />
            <div className="min-w-0">
              <div className="font-display font-bold text-gray-900 leading-tight truncate" data-testid="sidebar-brand-name">Noras Welt</div>
              <div className="text-[10px] tracking-wide text-gray-500 leading-tight" data-testid="sidebar-brand-tagline">Ihre Unterstützung im Alltag</div>
            </div>
          </div>
          <button className="md:hidden" onClick={() => setOpen(false)} aria-label="Menü schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            data-testid="search-open-btn"
            className="w-full flex items-center gap-3 px-3 py-2 mb-2 rounded-md text-sm text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-gray-900 border border-gray-200 transition-colors"
          >
            <Search className="w-4 h-4 shrink-0" />
            <span className="font-medium flex-1 text-left">Suchen…</span>
            <kbd className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded border border-gray-200 bg-white text-gray-500">⌘K</kbd>
          </button>
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-gray-900 text-white"
                    : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-gray-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9">
              {user?.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
              <AvatarFallback className="text-xs bg-black text-white">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate" data-testid="sidebar-user-name">
                {user?.name || "User"}
              </div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              aria-label="Abmelden"
              title="Abmelden"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 mt-14 md:mt-0">
        <div className="px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-7xl mx-auto" data-testid="main-content">
          {children}
        </div>
      </main>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
